/* eslint-disable max-len */
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  BidInfoProps, BidWorkSheetFormValues, CreateBidPayload, DriverExpensePayload, UpdateBidPayload,
} from '../../utils/types';
import { BidWorkSheetFormNames } from '../../utils/constants';
import { RootState } from '../../../../../redux/rootState';
import EndPoints from '../../../../../Routes/EndPoints';
import { getJobByIdStart, getSiteTypesStart } from '../../../jobStarter/addJob/redux/addJobSlice';
import {
  assignAnalystValues, assignCustomerValues, assignSiteValues, constructBidPricingPayload,
} from '../../utils/helper';
import { setLoader } from '../../../../../redux/loaderSlice';
import {
  createBidStart, getAnalystCityStart, getBidByIdStart, getBidPricingStart,
  getDriverExpenseStart, getNearestAnalystStart, setAirfareError, setUpdatedBidDetailsValues, updateBidStart,
} from '../../redux/bidWorksheetSlice';
import DialogBoxCustomerNotes from '../customerNotesPopup';
import BidInfoForm from './formComponent';
import { stripCustPrefix } from '../../../../../utils/CommonFunctions';
import { showErrorToaster } from '../../../../../Common/ComponentToast/ComponentSuccessToasts';

export default function BidInfo({ pwatch, scrollTop }:BidInfoProps) {
  const {
    control, setValue, watch, handleSubmit,
  } = useForm<BidWorkSheetFormValues>();
  const [isEdit, setIsEdit] = useState(false);
  const [open, setOpen] = useState(false);
  const {
    analystDetails, isLoading: bidLoader, selectedBid, nearestAnalystDetails, bidId: SelectedBidId, customerAnalyst, updatedBidDetails,
  } = useSelector((state:RootState) => state.bidsReducer);
  const { otherCustomerdetails, isLoading } = useSelector((state:RootState) => state.addCustomerReducer);
  const {
    selectedJob, selectedSite, isLoading: jobLoading,
  } = useSelector((state:RootState) => state.jobStarterReducer);
  const { pathname } = useLocation();
  const { bidId, jobId } = useParams();
  const dispatch = useDispatch();

  const driveFrom = watch(BidWorkSheetFormNames.drivenFrom);
  const roundMiles = watch(BidWorkSheetFormNames.roundTripMiles);
  const gulfCoastFL = watch(BidWorkSheetFormNames.gulfCoastFL);

  const bidPricingValues = pwatch();

  useEffect(() => {
    if (selectedBid?.BidId) {
      setIsEdit(true);
    } else if (pathname?.includes(EndPoints.ADD_JOB_BID)) {
      setIsEdit(false);
      dispatch(getJobByIdStart(String(jobId)));
    } else {
      dispatch(getBidByIdStart(String(bidId)));
      setIsEdit(true);
    }
    dispatch(getSiteTypesStart());
  }, [pathname]);

  const handleAction = () => {
    setOpen(!open);
  };

  useEffect(() => {
    dispatch(setLoader(isLoading || jobLoading || bidLoader));
  }, [isLoading, jobLoading, bidLoader]);

  useEffect(() => {
    if (selectedJob?.JobInfo) {
      setValue(BidWorkSheetFormNames.custId, stripCustPrefix(selectedJob?.JobInfo?.CustId));
      const payload = {
        custId: selectedJob?.JobInfo?.CustId || '',
        siteId: selectedJob?.JobInfo?.SiteId || '',
        bidId: SelectedBidId || '',
      };
      dispatch(getBidPricingStart(payload));
    }
  }, [selectedJob]);

  useEffect(() => {
    if (otherCustomerdetails?.CompanyInformation) {
      assignCustomerValues(setValue, otherCustomerdetails);
    }
  }, [otherCustomerdetails]);

  useEffect(() => {
    if (selectedSite?.SiteId) {
      assignSiteValues(setValue, selectedSite);
      if (!bidId) {
        setValue(BidWorkSheetFormNames.outOfCountry, selectedSite?.OutOfCountry);
      }
    }
    if (!SelectedBidId && selectedSite?.City) {
      dispatch(getNearestAnalystStart({ city: selectedSite?.City, state: selectedSite?.State }));
    }

    if (SelectedBidId && selectedSite?.City && !(analystDetails?.AnalystCities?.length > 0)) {
      const payload = {
        analystId: selectedBid?.BidDetails?.AnalystId,
        siteCity: selectedSite?.City,
        siteState: selectedSite?.State,
        calculateDistance: true,
      };
      dispatch(getAnalystCityStart(payload));
    }
  }, [selectedSite]);

  useEffect(() => {
    if (driveFrom && analystDetails?.BaseCity) {
      assignAnalystValues(setValue, analystDetails, driveFrom);
    }
  }, [driveFrom, analystDetails]);

  useEffect(() => {
    if (analystDetails?.AnalystCities) {
      setValue(BidWorkSheetFormNames.drivenFrom, analystDetails?.AnalystCities?.[0]?.Value);
    }
  }, [analystDetails]);

  useEffect(() => {
    if (customerAnalyst?.RoundTripMiles >= 0) {
      setValue(BidWorkSheetFormNames.roundTripMiles, customerAnalyst?.RoundTripMiles);
    }
  }, [customerAnalyst]);

  useEffect(() => {
    if (selectedJob?.JobInfo?.SiteId) {
      const IsBidNonMinimum = selectedBid?.BidDetails?.IsBidNonMinimum;
      const payload:DriverExpensePayload = {
        roundTripMiles: roundMiles || 0,
        calculatedDays: Number(bidPricingValues?.calculatedDays) || 0,
        overridedays: Number(bidPricingValues?.overRideDays) || 0,
        siteId: selectedJob?.JobInfo?.SiteId,
        gulfCoastFL: gulfCoastFL || false,
        airfare: Number(bidPricingValues?.airfare) || 0,
      };
      const payload2 = { ...payload, IsBidNonMinimum };

      dispatch(getDriverExpenseStart(selectedBid?.BidId ? payload2 : payload));
    }
  }, [roundMiles, bidPricingValues?.overRideDays, selectedJob, bidPricingValues?.calculatedDays, gulfCoastFL, bidPricingValues?.airfare]);

  useEffect(() => {
    if (nearestAnalystDetails?.NearestAnalyst && selectedSite.City) {
      setValue(BidWorkSheetFormNames.analystName, nearestAnalystDetails.NearestAnalyst);
      setValue(BidWorkSheetFormNames.roundTripMiles, nearestAnalystDetails.Distance);
    }
  }, [nearestAnalystDetails, selectedSite]);

  useEffect(() => {
    if (selectedBid.BidId) {
      if (Object.keys(updatedBidDetails)?.length > 0) {
        [
          { name: BidWorkSheetFormNames.analystName, value: updatedBidDetails?.analystName },
          { name: BidWorkSheetFormNames.outOfCountry, value: updatedBidDetails?.outOfCountry },
          { name: BidWorkSheetFormNames.drivenFrom, value: updatedBidDetails?.driveFrom },
          { name: BidWorkSheetFormNames.gulfCoastFL, value: updatedBidDetails?.gulfCoastFL },
          { name: BidWorkSheetFormNames.roundTripMiles, value: updatedBidDetails?.roundTripMiles },
        ].forEach(({ name, value }) => setValue(name, value));
      } else {
        [
          { name: BidWorkSheetFormNames.analystName, value: selectedBid?.BidDetails.AnalystId },
          { name: BidWorkSheetFormNames.outOfCountry, value: selectedBid?.BidDetails.IsOutOfCountry },
          { name: BidWorkSheetFormNames.drivenFrom, value: selectedBid?.BidDetails.DriveFrom },
          { name: BidWorkSheetFormNames.gulfCoastFL, value: selectedBid?.BidDetails.GulfCoastFL },
          { name: BidWorkSheetFormNames.roundTripMiles, value: selectedBid?.BidDetails.RoundTripMiles },
        ].forEach(({ name, value }) => setValue(name, value));
      }
    }
  }, [selectedBid]);

  const onSubmit = (values:BidWorkSheetFormValues) => {
    if (values.outOfCountry && !(Number(bidPricingValues.airfare) > 0)) {
      showErrorToaster('Airfare is required as the Job is based outside the US');
      dispatch(setAirfareError(true));
    } else {
      dispatch(setAirfareError(false));
      const bidPayload = {
        JobId: String(jobId),
        GulfCoastFL: values.gulfCoastFL || false,
        AnalystId: values.analystName || '0',
        DriveFrom: values.drivenFrom || '0',
        RoundTripMiles: Number(values.roundTripMiles) || 0,
        IsOutOfCountry: values.outOfCountry || false,
      };
      const bidPricingPayload = constructBidPricingPayload(bidPricingValues);
      if (isEdit) {
        // if (bidPricingValues.isSafetyCharges === true && !(Number(bidPricingValues.SafetyCharge) >= 0)) {
        //   showErrorToaster('Please enter Safety charges to proceed');
        // } else {
        const payload: UpdateBidPayload = {
          BidId: SelectedBidId,
          BidDetails: { ...bidPayload, JobId: selectedBid?.BidDetails.JobId },
          BidPriceDetails: bidPricingPayload,
        };
        dispatch(updateBidStart(payload));
        // }
      } else {
        const payload: CreateBidPayload = {
          BidDetails: bidPayload,
          BidPriceDetails: bidPricingPayload,
        };
        dispatch(createBidStart(payload));
      }
      scrollTop();
    }
  };

  const handleUpdatedData = () => {
    const values = watch();
    const pay = {
      analystName: values.analystName || '',
      driveFrom: values.drivenFrom || '',
      roundTripMiles: Number(values.roundTripMiles) || 0,
      gulfCoastFL: values.gulfCoastFL || false,
      outOfCountry: values.outOfCountry || false,
    };
    dispatch(setUpdatedBidDetailsValues(pay));
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <BidInfoForm control={control} handleUpdatedData={handleUpdatedData} handleAction={handleAction} watch={watch} scrollTop={() => {}} />
      </form>
      <DialogBoxCustomerNotes notes={otherCustomerdetails?.CompanyInformation?.CustomerNote || ''} show={open} setOpenFrom={handleAction} />
    </>
  );
}
